import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { StatusBadge } from './StatusBadge'
import { deriveVersionStatus, type VersionSummary } from '@/lib/api/frameworkConfig'
import { Pencil, Play } from 'lucide-react'
import type { VersionedLevelLabels } from './types'

interface VersionListProps {
  versions: VersionSummary[]
  isLoading: boolean
  labels: VersionedLevelLabels
  onEditDraft: (v: VersionSummary) => void
  onActivate: (v: VersionSummary) => void
}

export function VersionList({
  versions,
  isLoading,
  labels,
  onEditDraft,
  onActivate,
}: VersionListProps) {
  if (isLoading) {
    return <TableSkeleton />
  }

  if (versions.length === 0) {
    return (
      <Card>
        <CardContent className="py-4 text-center text-sm text-muted-foreground">
          {labels.noVersions}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-2">
      {versions.map((v) => {
        const status = deriveVersionStatus(v)
        return (
          <Card key={v.id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="font-medium">{labels.versionLabel(v.version_number)}</span>
                <StatusBadge
                  status={status}
                  labels={{
                    active: labels.statusActive,
                    draft: labels.statusDraft,
                    retired: labels.statusRetired,
                  }}
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {v.activated_at && <span>{new Date(v.activated_at).toLocaleDateString()}</span>}
                {status === 'draft' && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => onEditDraft(v)}>
                      <Pencil className="mr-1 h-3 w-3" />
                      {labels.editDraft}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onActivate(v)}>
                      <Play className="mr-1 h-3 w-3" />
                      {labels.activate}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
